/**
 * Data-layer adapters.
 *
 * Every feature package ships its UI with an INJECTED backend port (CatalogApi,
 * TeamsApi, AlertsSource, SearchProvider, NotificationSource, SavedViewsSource).
 * The dashboard fulfils them all with the shared `@authe/fe-commons` `ApiClient`
 * via the `createApi*` adapters, so there is exactly ONE HTTP brain (region
 * routing, failover, auth, envelope unwrap) behind the whole app — no per-feature
 * fetch code.
 *
 * These are hooks (not module singletons) because the `ApiClient` comes from
 * `<ApiProvider>` context; each is memoised on the client so the adapter is
 * stable across renders.
 */
import * as React from "react";
import { useApiClient } from "@authe/ui-core/api";
import { useSession } from "@authe/ui-core/session";
import { usePermissions } from "@authe/ui-core/permissions";
import type { AutheEvent, EventPresenterContext } from "@authe/fe-commons/events";
import { createApiCatalogApi, createCatalogLogoAdapter, type CatalogApi } from "@authe/catalog";
import { createApiTeamsApi, type TeamsApi } from "@authe/teams";
import { createApiSearchProvider } from "@authe/search";
import {
  createApiNotificationSource,
  mergeNotificationSources,
  notificationChannelsFor,
} from "@authe/notification-center";
import { subscribeDescriptor, useOptionalStreamingClient } from "@authe/streaming";
import { toNotificationEvent } from "./notificationEvent";
import { createApiSavedViewsSource } from "@authe/saved-views";
import { createApiInvestigationsSource } from "./investigationsSource";
import { createApiPipelinesSource } from "./pipelinesSource";
import { createApiTracersSource } from "./tracersSource";
import { createApiRunsSource } from "./runsSource";
import { createApiRelatedAddressesSource } from "./relatedAddressesSource";
import { createApiPipelineAlertsSource } from "./alertsSource";
import { createApiTenancySource } from "./tenancySource";
import { createApiOrgUsersSource } from "./orgUsersSource";
import { createApiAnalyticsSource } from "./analyticsSource";
import { createApiExplorerSource } from "./explorerSource";
import { createApiProtocolsSource } from "./protocolsSource";
import {
  createApiAbiByAddressClient,
  createApiAbiProvider,
  createApiAbiWriter,
  createApiContractSourceClient,
  createApiContractStateClient,
  createApiProxyResolver,
  createCompositeAbiProvider,
  createDefaultAbiProvider,
} from "@authe/contract-explorer";
import { createProxyOverridesClient } from "@authe/fe-commons/contracts";
import { createAddressesApi } from "@authe/fe-commons/addresses";
import { useFeatureFlags } from "@authe/ui-core/flags";
import { useOptionalGraphqlClient } from "@/app/graphql";
import { withGraphqlCatalogList } from "./graphqlCatalog";
import { withGraphqlAddresses } from "./graphqlAddresses";
import { restVisibility } from "@authe/fe-commons/attribution";
import {
  appConfig,
  RESOURCES,
  type AttributionResource,
  type AttributionScope,
} from "@/config";
import { useScopePrefs } from "@/app/scopePrefs";
import { useMyTeams } from "@/app/useMyTeams";
import { useQueryClient } from "@tanstack/react-query";
import type { AddressScope } from "@authe/fe-commons/graphql";
import type { BatchOverlays, OverlayRef } from "@authe/fe-commons/overlays";
import { addressOverlaysBatchQueryKey } from "@/features/overlays/useResolvedAddressOverlaysBatch";

/**
 * Wrap `CatalogApi.create` so an address-bound row (a label/tag/group/entity
 * created WITH an `address` + `chain`) is auto-attached to that address by the
 * backend at the caller's default visibility. We inject the ABAC carriers
 * (`user_scoped` / `visible_to_team_ids`) derived from the caller's effective
 * default write scope for that kind into the create body; `catalog-mgmt`'s
 * `_create_item` pops them off before persisting the row and uses them for the
 * `attach_overlay` it fires. Keeping the scope here (not hardcoded server-side)
 * is what makes the default a single, now user-editable, knob. The `address`
 * kind is the address row itself (not an overlay on an address), so it's never
 * auto-attached.
 *
 * The carriers come from the same `restVisibility` mapper an explicit attach
 * uses, which is the point: this path used to build them itself and pass no
 * team ids, so creating an address-bound row with "team" configured produced a
 * private attach the caller's team could not see. A scope means one thing on
 * the wire, defined once.
 *
 * One honest limit: there is no case context down here, so the shared-case
 * raise `useAttributionController` applies does not reach this path. Creating a
 * row inside a shared case still auto-attaches at the caller's own default.
 */
function withCatalogAutoAttachScope(
  api: CatalogApi,
  resolveDefaultWriteScope: (resource: AttributionResource) => AttributionScope,
  teamIds: string[],
): CatalogApi {
  return {
    ...api,
    create(kind, body) {
      const address = body?.address;
      const chain = body?.chain;
      const addressBound =
        kind !== "address" &&
        typeof address === "string" &&
        address !== "" &&
        typeof chain === "string" &&
        chain !== "";
      if (!addressBound) return api.create(kind, body);
      const scope = resolveDefaultWriteScope(kind as AttributionResource);
      return api.create(kind, { ...body, ...restVisibility({ scope, teamIds }) });
    },
  };
}

/**
 * The catalog port. Reads (`list`) take the fast GraphQL path when the transport
 * registry enables it (`graphql.enabled` + `graphql.catalog.list`), falling back
 * to REST on any GraphQL error; writes + logos always go through REST. When no
 * GraphQL client is configured the wrapper is a pure pass-through.
 */
export function useCatalogApi() {
  const client = useApiClient();
  const graphqlClient = useOptionalGraphqlClient();
  const flags = useFeatureFlags();
  const { resolveDefaultWriteScope } = useScopePrefs();
  const teams = useMyTeams();
  const teamIds = React.useMemo(() => teams.map((t) => t.id), [teams]);
  return React.useMemo(() => {
    // Catalog rows live in catalog-mgmt; logos go through the presigned-asset
    // flow (not a JSON POST), so wire the host's logo adapter (S3 upload ticket
    // + CDN preview) onto the otherwise-network-free CatalogApi.
    const logo = createCatalogLogoAdapter({ client, cdnBase: appConfig.cdnUrl });
    const rest = createApiCatalogApi(client, {
      uploadLogo: logo.uploadLogo,
      resolveLogoUrl: logo.resolveLogoUrl,
    });
    return withCatalogAutoAttachScope(
      withGraphqlCatalogList(rest, { graphqlClient, flags }),
      resolveDefaultWriteScope,
      teamIds,
    );
  }, [client, graphqlClient, flags, resolveDefaultWriteScope, teamIds]);
}

/**
 * The typed `address-mgmt-api` client (`@authe/fe-commons/addresses`) over the
 * shared `ApiClient`. Speaks canonical `/addresses/...` paths; `RESOURCE_REWRITES`
 * maps them to the private tier (see the address-mgmt-api routing gaps ADDR1/ADDR2
 * in `front-ends/docs/backend-prereqs.md`).
 *
 * The IDEMPOTENT overlay/membership ops (assignment attach/detach + list,
 * primary set/clear, entity-mapping get/put/delete) take the fast GraphQL path
 * when the transport registry enables them (`graphql.enabled` + the per-op flag,
 * e.g. `graphql.address.assignments.write`), falling back to REST on any GraphQL
 * error; every non-idempotent write stays on REST. With no GraphQL client the
 * wrapper is a pure pass-through.
 *
 * `list` over GraphQL also brings back each row's resolved overlays, which the
 * REST list cannot, so we seed the overlay batch cache with them: a table that
 * draws attribution chips stops running a second `POST /addresses/catalog/resolve`
 * after every page and every "Load more". The seed is written at the caller's
 * configured default view scope, the same tier the batch hook would have asked
 * for — a seed at any other tier is one nobody reads.
 */
export function useAddressesApi() {
  const client = useApiClient();
  const graphqlClient = useOptionalGraphqlClient();
  const flags = useFeatureFlags();
  const queryClient = useQueryClient();
  const { resolveDefaultResolveScope } = useScopePrefs();

  // `USER` IS the un-widened read, so it maps back to undefined — keeping the
  // request, and its cache key, identical to the plain default read. Mirrors
  // `useResolvedAddressOverlaysBatch`; the two disagreeing about the same
  // setting is what the seed would otherwise silently miss on.
  const effectiveScope = resolveDefaultResolveScope("address");
  const resolveScope = effectiveScope === "USER" ? undefined : effectiveScope;

  const seedOverlays = React.useCallback(
    (overlays: BatchOverlays, refs: OverlayRef[], scope?: AddressScope) => {
      // Seeded under BOTH `includeRow` keys, because the two are not the same
      // answer by contract: a batch fetched without it has empty row maps, and a
      // consumer that did not ask for rows must not start seeing them. The page
      // carried the rows either way, so the only cost is the empty-map variant.
      queryClient.setQueryData(addressOverlaysBatchQueryKey(refs, true, scope), overlays);
      queryClient.setQueryData(addressOverlaysBatchQueryKey(refs, false, scope), {
        ...overlays,
        rowByKey: new Map(),
        rowByAddress: new Map(),
      });
    },
    [queryClient],
  );

  return React.useMemo(
    () =>
      withGraphqlAddresses(createAddressesApi(client), {
        graphqlClient,
        flags,
        seedOverlays,
        resolveScope,
      }),
    [client, graphqlClient, flags, seedOverlays, resolveScope],
  );
}

/**
 * The `related-addresses-mgmt` port: address clusters, the evidenced links they
 * are concluded from, and the runs that produce them.
 *
 * Plain, unlike {@link useAddressesApi} — there is no GraphQL fast path and
 * nothing to seed, because a cluster read is one keyed lookup rather than a page
 * of rows a table would then re-resolve overlays for.
 */
export function useRelatedAddressesApi() {
  const client = useApiClient();
  return React.useMemo(() => createApiRelatedAddressesSource(client), [client]);
}

/**
 * The `teams-mgmt-api` port, with a display-name backfill on `listMembers`.
 *
 * A team member row is a snapshot written at seed/creation time; a tenant
 * bootstrapped by the identity seeder stamped the owner as the placeholder
 * "Seed Owner" (it never captured the SSO name). We resolve names from live
 * identity instead: the current user is authoritative from the session, and any
 * lingering "Seed Owner" on someone else degrades to their email local-part.
 * (The backend seeder now records the real name, so this is a transitional net
 * for already-seeded rows.)
 */
export function useTeamsApi(): TeamsApi {
  const client = useApiClient();
  const { session } = useSession();
  const selfId = session?.user.id ?? session?.claims.user_id ?? null;
  const selfName = session?.user.name ?? session?.names.userName ?? null;
  const selfEmail = session?.user.email ?? null;
  return React.useMemo<TeamsApi>(() => {
    const api = createApiTeamsApi(client);
    return {
      ...api,
      async listMembers(team: Parameters<typeof api.listMembers>[0]) {
        const members = await api.listMembers(team);
        return members.map((member) => {
          if (selfId && member.id === selfId) {
            return {
              ...member,
              name: selfName ?? member.name,
              email: member.email ?? selfEmail ?? undefined,
            };
          }
          if ((member.name ?? "").trim().toLowerCase() === "seed owner") {
            const local = member.email ? member.email.split("@")[0] : "";
            return { ...member, name: local || member.email || member.name };
          }
          return member;
        });
      },
    };
  }, [client, selfId, selfName, selfEmail]);
}

/**
 * The `trigger-mgmt-api` alerts port (trigger rules + destinations + fired
 * history). Crosses the camelCase-`AlertRule` ⇆ snake_case-`TriggerRuleRow`
 * boundary and uses POST-create / PATCH-update against
 * `/v0/{triggers,destinations,alerts}` (mapped from canonical `/alert-*` by
 * `RESOURCE_REWRITES`) — see `./alertsSource`.
 */
export function useAlertsSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiPipelineAlertsSource(client), [client]);
}

export function useSearchProvider() {
  const client = useApiClient();
  return React.useMemo(() => createApiSearchProvider(client), [client]);
}

/**
 * The `metadata-registry-api` saved-dashboards port (Datadog-style analytics
 * documents CRUD, resolved across the tenancy ladder). Speaks canonical
 * `/metadata-dashboards*` paths (mapped to `internal/metadata-registry-api` by
 * `RESOURCE_REWRITES`) and parses each blob through the shared
 * `@authe/fe-commons/metadata-analytics` schema — see `./analyticsSource`.
 * Per-panel data fetching is owned by `@authe/analytics-widgets`, not this port.
 */
export function useAnalyticsSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiAnalyticsSource(client), [client]);
}

/**
 * The transaction-explorer WRITE port — promote a browsed field to a facet,
 * assemble facets into a view, and materialize authored views into physical
 * `mv_*` relations. The explorer READS via `@authe/analytics-widgets` hooks
 * over the same client; this port only carries the mutations (see
 * `./explorerSource`).
 */
export function useExplorerSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiExplorerSource(client), [client]);
}

/**
 * The protocol-definition port — read the decoding catalogue as a rung resolves
 * it, and author / drop that rung's `definition_patch`. The write counterpart of
 * the `definition_scope` marker the explorer puts on a decoded fact (see
 * `./protocolsSource`).
 */
export function useProtocolsSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiProtocolsSource(client), [client]);
}

/**
 * Notification inbox source. History + the read/archive mutations come from the
 * `notification_history` REST resource; live push is layered on via
 * `@authe/streaming` whenever a stream client is mounted (Live mode on). We
 * subscribe to the caller's tenant-wide, user, and team notification channels
 * (`notificationChannelsFor`) — matching the publisher's audience fan-out — and
 * feed each event into the inbox via `mergeNotificationSources`. With no stream
 * client (Live off) this degrades to history-only, untouched.
 *
 * `teamIds` is `[]` until team membership is surfaced in the session (the JWT
 * carries no `team_ids` claim yet); the tenant + user channels still flow.
 */
export function useNotificationSource() {
  const client = useApiClient();
  const stream = useOptionalStreamingClient();
  const { session } = useSession();

  const orgId = session?.tenant.orgId ?? null;
  const tenantId = session?.tenant.tenantId ?? null;
  const userId = session?.user.id ?? session?.claims.user_id ?? null;

  return React.useMemo(() => {
    const base = createApiNotificationSource(client);
    if (!stream || (!orgId && !tenantId && !userId)) return base;

    const channels = notificationChannelsFor({ orgId, tenantId, userId }, []);
    const live = {
      subscribe(onEvent: (event: AutheEvent) => void) {
        const unsubs = channels.map((id) =>
          stream.subscribeChannel(subscribeDescriptor(id), (message) => {
            const event = toNotificationEvent(message.payload);
            if (event) onEvent(event);
          }),
        );
        return () => {
          for (const off of unsubs) off();
        };
      },
    };
    return mergeNotificationSources(base, live);
  }, [client, stream, orgId, tenantId, userId]);
}

/**
 * Presenter context for the notification inbox (bell + Activity page) built from
 * the live session. Lets the shared `describeEvent` render the signed-in user's
 * *own* events as "you" / "Signed in as <name>" (using the JWT `name`) instead
 * of the opaque `google-oauth2|…` principal, and tag the org/tenant they're in.
 * Other principals stay anonymised — the JWT only names the current user.
 */
export function useNotificationPresenter(): EventPresenterContext {
  const { session } = useSession();
  return React.useMemo<EventPresenterContext>(() => {
    const currentUserId = session?.user.id ?? null;
    const currentUserName = session?.user.name ?? session?.user.email ?? null;
    return {
      currentUserId,
      resolveActorName: (actorId) =>
        currentUserId && actorId === currentUserId ? currentUserName : null,
    };
  }, [session]);
}

export function useSavedViewsSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiSavedViewsSource(client), [client]);
}

/**
 * The investigations index port, backed by `investigation-mgmt-api`.
 *
 * Cases were saved views at `tableId: "investigations"` until they outgrew that:
 * a saved view has no by-id read (so opening one case listed every case in the
 * tenant), it is one 400 KB item (so evidence, annotations, comments and workflow
 * had nowhere to grow), and it authorized under `shared-graphs` (so "rename a
 * saved graph" and "publish this annotation tenant-wide" were the same grant).
 *
 * The collaborative half — notes, discussion, the co-edited workflow — is a Yjs
 * room, and that room is now persisted too: `/investigations/:id/collab` hydrates
 * a joiner and accepts published updates, so a case survives the last browser tab
 * closing. See `./investigationsSource`.
 */
export function useInvestigationsSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiInvestigationsSource(client), [client]);
}

/**
 * The `management-api` tenancy port (organizations + the tenants under the
 * active org). Backs the Settings "Organizations & tenants" section and the
 * top-bar tenant switcher's friendly names. Speaks canonical `/organizations/*`
 * paths (rewritten onto the `.management.` zone by `RESOURCE_REWRITES`) — see
 * `./tenancySource`.
 */
export function useTenancySource() {
  const client = useApiClient();
  return React.useMemo(() => createApiTenancySource(client), [client]);
}

/**
 * The `permissions-mgmt-api` org-user port (organization members + org role
 * catalog + invite). Backs the Team page's "Organization members" list and the
 * "Invite people" dialog. Speaks the dashboard-local canonical prefixes
 * `/org-users[/invite]` + `/org-roles` (rewritten onto the `.management.`
 * permissions zone in `app/api-client.ts`) — see `./orgUsersSource`.
 */
export function useOrgUsersSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiOrgUsersSource(client), [client]);
}

/**
 * The `pipeline-mgmt-api` port for the pipeline builder (catalog + definitions
 * CRUD + server-side validate/dry-run). Crosses the camelCase-builder ⇆
 * snake_case-wire boundary via the `@authe/fe-commons/pipeline` mappers; speaks
 * canonical `/pipelines/*` paths (mapped to `private/pipeline-mgmt-api` by
 * `RESOURCE_REWRITES`).
 */
export function usePipelinesSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiPipelinesSource(client), [client]);
}

/**
 * The `pipeline-mgmt-api` tracers port (flow-tracking definitions + their run
 * snapshots). Each run embeds an `InteractionGraph` that `@authe/tx-graph`
 * renders; this adapter crosses the camelCase ⇆ snake_case boundary and speaks
 * canonical `/tracers/*` paths (mapped to `private/pipeline-mgmt-api` by
 * `RESOURCE_REWRITES`).
 */
export function useTracersSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiTracersSource(client), [client]);
}

/**
 * The `pipeline-mgmt-api` run-jobs port (async forward-ingest + fan-out). Submits
 * a batch ingest (fixed payloads / latest-N / tx hashes), lists prior jobs, and
 * polls one job's status + per-tx outcomes so the run console can follow a
 * long-running ingest across a refresh. Speaks canonical `/runs/*` paths (mapped
 * to `private/pipeline-mgmt-api` by `RESOURCE_REWRITES`) — see `./runsSource`.
 */
export function useRunsSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiRunsSource(client), [client]);
}

/**
 * The backend contract-state reader for the `@authe/contract-explorer`
 * "On-chain state" tab. POSTs to the `get-contract-state` lambda (canonical
 * `/abis/contract-state` → `/private/abis/v0/contract-state` via the shared
 * `ApiClient` routing) and returns the category-aware, proxy-merged state tree.
 * Without it that tab shows an unconfigured prompt (there is no RPC fallback).
 */
export function useContractStateClient() {
  const client = useApiClient();
  return React.useMemo(() => createApiContractStateClient(client), [client]);
}

/**
 * ABI resolver for the contract explorer. Composite: the caller's stored /
 * inherited / system-default ABIs from `abi-mgmt-api` (`GET /abis/{chain}/{address}`)
 * first, then the ERC20/721/1155 defaults as a fallback. Replaces the old
 * defaults-only provider so verified ABIs actually surface.
 */
export function useAbiProvider() {
  const client = useApiClient();
  return React.useMemo(
    () => createCompositeAbiProvider([createApiAbiProvider(client), createDefaultAbiProvider()]),
    [client],
  );
}

/**
 * Backend ABI writer (save + refresh) for the explorer's Edit tab. Permission-
 * gated on `abis:edit`/`abis:create`; the backend does copy-on-write when the
 * edited ABI is inherited/system-default, so a save never mutates the shared row.
 */
export function useAbiWriter() {
  const client = useApiClient();
  const { can } = usePermissions();
  const canEdit = can(RESOURCES.abis, "edit") || can(RESOURCES.abis, "create");
  return React.useMemo(() => createApiAbiWriter(client, { canEdit }), [client, canEdit]);
}

/**
 * Server-side proxy resolver for the explorer — `GET /proxy-overrides/{chain}/
 * {address}` on proxy-mgmt, which answers from the caller's override ladder first
 * and the shared point-in-time engine second. Needs `proxy:resolve`.
 */
export function useProxyResolver() {
  const client = useApiClient();
  return React.useMemo(() => createApiProxyResolver(client), [client]);
}

/**
 * Proxy override authoring (`proxy-mgmt`) for the explorer's Proxies tab: resolve
 * with provenance, the upgrade timeline, and the pin/unpin writes.
 *
 * Separate from {@link useProxyResolver}, which maps a resolved chain onto the
 * explorer's `ProxyChain` for display. This one keeps the wire row intact —
 * scope, evidence, block window — because that is exactly what an operator needs
 * in order to decide whether to override it.
 */
export function useProxyOverridesClient() {
  const client = useApiClient();
  return React.useMemo(() => createProxyOverridesClient(client), [client]);
}

/**
 * The two proxy authoring permissions, read once so the page and the panel agree.
 * `edit` pins an implementation for the caller's own rung; `delete` purges the
 * SHARED discovery rows, which is why it is checked separately rather than folded
 * into `edit`.
 */
export function useProxyPermissions(): { canEdit: boolean; canPurge: boolean } {
  const { can } = usePermissions();
  return React.useMemo(
    () => ({ canEdit: can(RESOURCES.proxy, "edit"), canPurge: can(RESOURCES.proxy, "delete") }),
    [can],
  );
}

/**
 * Lists every ABI visible for a contract (`GET /abis/by-address/{chain}/{address}`):
 * the caller's overrides across the scope ladder + the public catalog row.
 * Powers the explorer's "Sources" picker.
 */
export function useAbiByAddressClient() {
  const client = useApiClient();
  return React.useMemo(() => createApiAbiByAddressClient(client), [client]);
}

/**
 * Verified source code + (opt-in) deployed bytecode for the explorer's "Source"
 * tab (`GET /source/{chain}/{address}` → `/private/abis/v0/source/…`). The
 * backend fans out to third-party verifiers (Codeslaw / Etherscan / Blockscout /
 * Sourcify) and reconciles the best verified source.
 */
export function useContractSourceClient() {
  const client = useApiClient();
  return React.useMemo(() => createApiContractSourceClient(client), [client]);
}
